import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { DndProvider } from "react-dnd-multi-backend";

import { ArcadeInstructionsModal } from "../shared/components/arcade-instructions-modal/arcade-instructions-modal.component";
import { useArcadeInstructionsModal } from "../shared/components/arcade-instructions-modal/use-arcade-instructions-modal";
import { ArcadeShield } from "../shared/components/arcade-shield/arcade-shield.component";
import { useRewardedVideoAd } from "../shared/hooks/use-rewarded-video-ad";
import { ArcadeGames } from "../shared/types";
import {
  getArcadeGameImageUrl,
  retrieveFromSessionStorage,
  saveInSessionStorage,
} from "../shared/utils";

import { PuzzleBanner } from "./components/puzzle-banner/puzzle-banner.component";
import { PuzzleCanvas } from "./components/puzzle-canvas/puzzle-canvas.component";
import { PuzzleInfo } from "./components/puzzle-info/puzzle-info.component";
import { PuzzleSlices } from "./components/puzzle-slices/puzzle-slices.component";
import { usePuzzle } from "./widget.state";
import { PuzzleRoundStatusEnum } from "./widget.types";

export function Widget() {
  const {
    status,
    data,
    scaledImage,
    setData,
    handleTimeout,
    handleStartGame,
    handleNextRound,
  } = usePuzzle();

  const advanceToNextRound = useCallback(() => handleNextRound(false), [handleNextRound]);

  const { triggerAd } = useRewardedVideoAd({
    onAdGranted: advanceToNextRound,
    onAdDismissed: advanceToNextRound,
    onAdFailed: advanceToNextRound,
  });

  // Trigger rewarded video ad when the round is finalized
  const hasTriggeredAd = useRef(false);
  useEffect(() => {
    if (
      (status === PuzzleRoundStatusEnum.WON ||
        status === PuzzleRoundStatusEnum.LOST) &&
      !hasTriggeredAd.current
    ) {
      hasTriggeredAd.current = true;
      setTimeout(() => triggerAd(), 3000);
    }
  }, [status, triggerAd]);

  const [
    showInstructionsModal,
    handleHideInstructionsModal,
  ] = useArcadeInstructionsModal(ArcadeGames.Puzzle);
  const arcadeAvailableGames = window.FKL.arcadeAvailableGamesInfo;

  const sessionEntries = useMemo(() => {
    const SWEEPSTAKE_SESSION_ENTRIES = "sweepstakeSessionEntries";
    const sweepstakeSessionEntries = retrieveFromSessionStorage(
      SWEEPSTAKE_SESSION_ENTRIES
    );
    if (!sweepstakeSessionEntries) {
      saveInSessionStorage(SWEEPSTAKE_SESSION_ENTRIES, 0);
      return 0;
    }
    if (data.correctTiles > 0) {
      saveInSessionStorage(
        SWEEPSTAKE_SESSION_ENTRIES,
        Number(sweepstakeSessionEntries) + 1
      );
      return Number(sweepstakeSessionEntries) + 1;
    }
    return Number(sweepstakeSessionEntries);
  }, [data.correctTiles]);

  const redeemedEntries = useCallback(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return Number(submission?.data?.redeemedEntries || 0);
  }, []);

  return (
    <>
      <ArcadeShield
        className="arcade__puzzle"
        roundIndex={data.roundIndex}
        header={{
          allTimeEntries: data.allTimePoints,
          extraEntries: data.correctTiles,
          redeemedEntries: redeemedEntries(),
          sessionEntries,
        }}
      >
        <PuzzleBanner
          correctTiles={data.correctTiles}
          totalTiles={data.totalSlices}
          brandName={data.brandName}
          logoUrl={data.logoUrl}
          brandShortDescription={data.brandShortDescription}
          status={status}
          onTimeout={handleTimeout}
        />
        <DndProvider options={HTML5toTouch}>
          <PuzzleCanvas
            slicesPerRow={data.slicesPerRow}
            arrangedSlices={data.arrangedSlices}
            imageSlices={scaledImage.slices}
            puzzleImage={data.puzzleImage}
            puzzleOriginalImageWidth={data.puzzleOriginalImageWidth}
            puzzleOriginalImageHeight={data.puzzleOriginalImageHeight}
            imageWidth={scaledImage.imageWidth}
            imageHeight={scaledImage.imageHeight}
            sliceWidth={scaledImage.sliceWidth}
            sliceHeight={scaledImage.sliceHeight}
            setData={setData}
            status={status}
          />
          <PuzzleSlices
            slicesPerRow={data.slicesPerRow}
            totalRows={data.totalRows}
            pendingSlices={data.pendingSlices}
            imageSlices={scaledImage.slices}
            puzzleImage={data.puzzleImage}
            puzzleOriginalImageWidth={data.puzzleOriginalImageWidth}
            puzzleOriginalImageHeight={data.puzzleOriginalImageHeight}
            imageWidth={scaledImage.imageWidth}
            imageHeight={scaledImage.imageHeight}
            sliceWidth={scaledImage.sliceWidth}
            sliceHeight={scaledImage.sliceHeight}
            scaleFactor={data.scaleFactor}
            status={status}
            setData={setData}
            onStart={handleStartGame}
          />
        </DndProvider>
        <PuzzleInfo
          title={data.title}
          brandDescription={data.brandDescription}
        />
      </ArcadeShield>
      <ArcadeInstructionsModal
        isOpen={showInstructionsModal}
        title="Product Puzzle"
        subtitle=""
        image={getArcadeGameImageUrl(
          arcadeAvailableGames,
          document.location.href
        )}
        instructions={
          <>
            Finish the picture to win points before the timer runs out. You will
            have <strong>45 seconds</strong> to assemble the drag &amp; drop
            tiles, with a <strong>total of {data.totalSlices} points</strong>.
            You will earn{" "}
            <strong>+1 point for each correctly placed tile</strong>. Good luck!
          </>
        }
        onHideInstructionsModal={handleHideInstructionsModal}
      />
    </>
  );
}
